import { useQuery, useQueryClient, UseQueryResult } from '@taxdown/zeno-sama-query';
import Notify, { AlertTypes } from 'Services/notification';
import SatService from 'Services/sat';
import i18n from 'Services/i18n';
import { PueblaPreviousYearsResponse } from 'Services/sat/types';
import StorageService from 'Services/storage';
import { history } from 'Store';
import { PreviousYearsResultType, PreviousYearStatus } from 'Containers/PreviousYears/types';
import { routes } from 'Constants/routes';
import { ErrorResponseType, QueryKeys, ResponseType } from 'Types/common';

type PreviousYearHookData = {
  records: PueblaPreviousYearsResponse['records'];
  amountTotal: number;
};

export const useFetchPollingCalculatingPreviousYears = (
  isPreviousYear: boolean
): UseQueryResult<ResponseType<PueblaPreviousYearsResponse>> =>
  useQuery([QueryKeys.CALCULATING_LOADER], SatService.getPueblaPreviousYears, {
    onSuccess: data => {
      const response = data.data;
      if (
        response?.item?.records?.length > 0 &&
        response?.item?.stage === PreviousYearStatus.READY
      ) {
        const route =
          response?.item?.refundStatusType === PreviousYearsResultType.TO_RETURN
            ? routes.PREVIOUS_YEARS
            : routes.PREVIOUS_YEARS_DIFFERENCE;
        history.push(route);
      }
    },
    onError: (error: unknown) => {
      const errorData = error as ErrorResponseType;
      const errorDetail =
        errorData?.response?.data?.detail || i18n.t('py:PreviousYear.polling.error');
      Notify.sendNotification(errorDetail, AlertTypes.error);
    },
    refetchInterval: data => {
      const response = data?.data;
      if (!response?.item) {
        return 500;
      }
      if (response?.item && response?.item?.stage === PreviousYearStatus.CALCULATING) {
        return 500;
      }
      return false;
    },
    cacheTime: 0,
    enabled: !!isPreviousYear,
    refetchOnWindowFocus: false,
  });

export const usePreviousYearData = (): PreviousYearHookData | undefined => {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<ResponseType<PueblaPreviousYearsResponse>>([
    QueryKeys.FETCH_PREVIOUS_YEARS,
  ]);

  if (data?.data?.item) {
    const info = {
      records: data.data.item.records,
      amountTotal: data.data.item.amountTotal,
    };
    StorageService.setItem('previousYearData', JSON.stringify(info));
    return info;
  }

  const storedData = StorageService.getItem('previousYearData');
  return storedData ? (JSON.parse(storedData) as PreviousYearHookData) : undefined;
};
